/**
 * Passenger/cargo journey choices, provisional loads, and final assignment payloads.
 *
 * @remarks
 * Groups compatible choices and applies the selected visit pairs through the shared
 * assignment planner. A requested direct journey adds visits before assigning its
 * passengers/cargo; route-only visit operations belong to itinerary.ts.
 * These helpers do not render UI or perform network requests.
 *
 * @packageDocumentation
 */
import {
    AssignmentPlan,
    AssignmentRow,
    assignmentKey,
    chooseAssignment,
    legsFromAssignmentPlan,
    rangeIndexes,
} from '../../../../../common/flight-leg/assignment-plan';
import {createVisit} from '../../../../../common/flight-leg/route-visits';
import {replanVisits} from './itinerary';

/**
 * Groups assignments with the same scheduled endpoints and available visit pairs.
 *
 * @param reviewKeys - Keeps previously resolved rows visible during review when supplied.
 */
export function journeyGroups(plan: AssignmentPlan, reviewKeys?: string[]): AssignmentRow[][] {
    const groups = new Map<string, AssignmentRow[]>();

    plan.rows
        .filter((row) =>
            reviewKeys ? reviewKeys.includes(assignmentKey(row)) : row.status === 'review'
        )
        .forEach((row) => {
            const entity = (row.kind === 'pax' ? plan.pax : plan.cargo).find(
                (entity) => entity._id === row.id
            );
            const signature = JSON.stringify([
                row.kind,
                entity.departureID,
                entity.destinationID,
                row.candidates.map((range) => [range.from.key, range.to.key]),
            ]);
            groups.set(signature, [...(groups.get(signature) || []), row]);
        });

    return Array.from(groups.values());
}

/** Repeated flight locations matter here only when they offer distinct journey endpoints. */
export function repeatedJourneyLocations(rows: AssignmentRow[]): string[] {
    return Array.from(
        new Set(
            rows.flatMap((row) =>
                (['from', 'to'] as const).flatMap((endpoint) => {
                    const visits = Array.from(
                        new Map(
                            row.candidates.map((range) => [range[endpoint].key, range[endpoint]])
                        ).values()
                    );
                    return visits
                        .filter((visit) =>
                            visits.some(
                                (other) =>
                                    other.key !== visit.key && other.locationID === visit.locationID
                            )
                        )
                        .map((visit) => visit.locationID);
                })
            )
        )
    );
}

/** Returns the shortest available journey's candidate index, or -1 when none exists. */
export function suggestedCandidate(plan: AssignmentPlan, row: AssignmentRow): number {
    let shortest = Infinity;
    let selected = -1;

    row.candidates.forEach((range, index) => {
        const [from, to] = rangeIndexes(plan.visits, range);
        if (to - from < shortest) {
            shortest = to - from;
            selected = index;
        }
    });

    return selected;
}

/**
 * Computes preview loads using provisional choices for unresolved assignments.
 *
 * @remarks
 * Suggestions never confirm the actual plan or make it eligible to save.
 */
export function suggestedLegs(plan: AssignmentPlan, names: Map<string, string>) {
    let display = plan;

    plan.rows
        .filter((row) => row.status === 'review')
        .forEach((row) => {
            const candidate = suggestedCandidate(plan, row);
            if (candidate >= 0) display = chooseAssignment(display, assignmentKey(row), candidate);
        });

    return legsFromAssignmentPlan(display, names, true);
}

/** Applies the same visit pair, or explicit unassignment, to every row in a journey group. */
export function confirmJourney(
    plan: AssignmentPlan,
    rows: AssignmentRow[],
    choice: number | 'unassign'
) {
    const range = choice === 'unassign' ? undefined : rows[0].candidates[choice];
    if (choice !== 'unassign' && !range) throw new Error('Choose departure and arrival visits.');

    return rows.reduce((next, row) => {
        const current = next.rows.find((item) => assignmentKey(item) === assignmentKey(row));
        const index =
            range &&
            current.candidates.findIndex(
                (item) => item.from.key === range.from.key && item.to.key === range.to.key
            );
        return chooseAssignment(next, assignmentKey(row), choice === 'unassign' ? choice : index);
    }, plan);
}

/** Add a direct journey before the existing final return, preserving every authored visit. */
export function addJourneyVisits(
    plan: AssignmentPlan,
    rows: AssignmentRow[],
    roundTrip: boolean,
    allowExtension: boolean
) {
    if (!allowExtension) throw new Error('Choose Extend the route before adding visits.');

    const row = rows[0];
    const entity = (row.kind === 'pax' ? plan.pax : plan.cargo).find(
        (entity) => entity._id === row.id
    );
    const visits = [...plan.visits];

    const finalReturn =
        roundTrip &&
        visits.length > 1 &&
        visits[0].locationID === visits[visits.length - 1].locationID
            ? visits.pop()
            : undefined;
    if (visits[visits.length - 1]?.locationID !== entity.departureID)
        visits.push(createVisit(entity.departureID));
    const from = visits[visits.length - 1];
    const to = createVisit(entity.destinationID);
    visits.push(to);
    if (finalReturn) visits.push(finalReturn);

    let next = replanVisits(plan, visits);
    rows.forEach((item) => {
        const current = next.rows.find(
            (candidate) => assignmentKey(candidate) === assignmentKey(item)
        );
        const candidate = current.candidates.findIndex(
            (range) => range.from.key === from.key && range.to.key === to.key
        );
        next = chooseAssignment(next, assignmentKey(item), candidate);
    });

    return next;
}

/** Builds the existing per-leg mutation payload, rejecting unresolved assignments. */
export function assignmentPayload(plan: AssignmentPlan, names: Map<string, string>) {
    const legs = legsFromAssignmentPlan(plan, names);

    return {
        legs,
        paxIDList: Array.from(new Set(legs.flatMap((leg) => leg.paxIDs))),
        cgoIDList: Array.from(new Set(legs.flatMap((leg) => leg.cgoIDs))),
    };
}
